import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, ReportView } from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';
import { getCrewAiOverview } from '../../../services/mutations';
import { moduleConfig } from '../config';
import { parseCrewRouteId } from '../crewRouteId';
import { crewDisplayName } from '../formMappers';
import { useCrewFile } from '../useData';
import { CrewFileReport } from './CrewFileReport';
import { CrewFilePrintSectionsModal } from './CrewFilePrintSectionsModal';
import {
  CREW_FILE_PRINT_SECTIONS,
  countSelectedCrewFilePrintSections,
  crewFilePrintSectionsToParam,
  parseCrewFilePrintSectionsParam,
  type CrewFilePrintSectionSelection,
} from './crewFilePrintSections';

type ManagementCompanyRow = {
  id: number;
  name: string;
  isActive?: boolean;
  logoStorageKey?: string | null;
};

const CrewFileReportPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const crewId = parseCrewRouteId(id);
  const { data: item, isLoading, isError } = useCrewFile(crewId);
  const { data: aiOverview } = useSWR(
    crewId ? `crew-ai-overview-print-${crewId}` : null,
    () => getCrewAiOverview(crewId!),
    { revalidateOnFocus: false },
  );
  const { data: companiesRaw } = useSWR('/management-companies?page=1&limit=50', fetcher, {
    revalidateOnFocus: false,
  });

  const [sections, setSections] = useState<CrewFilePrintSectionSelection>(() =>
    parseCrewFilePrintSectionsParam(searchParams.get('sections')),
  );
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setSections(parseCrewFilePrintSectionsParam(searchParams.get('sections')));
  }, [searchParams]);

  const managementCompany = useMemo(() => {
    const list = normalizePaginatedListResponse<ManagementCompanyRow>(companiesRaw, 1, 50).data;
    const active = list.filter((c) => c.isActive !== false);
    const withLogo = active.find((c) => c.logoStorageKey) ?? list.find((c) => c.logoStorageKey);
    return withLogo?.logoStorageKey
      ? { id: withLogo.id, name: withLogo.name, logoStorageKey: withLogo.logoStorageKey }
      : null;
  }, [companiesRaw]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const selectedCount = countSelectedCrewFilePrintSections(sections);

  const syncSectionsUrl = (next: CrewFilePrintSectionSelection) => {
    if (!crewId) return;
    const q = new URLSearchParams();
    if (countSelectedCrewFilePrintSections(next) < CREW_FILE_PRINT_SECTIONS.length) {
      q.set('sections', crewFilePrintSectionsToParam(next));
    }
    const qs = q.toString();
    navigate(`${moduleConfig.module.route.filePrint(crewId)}${qs ? `?${qs}` : ''}`, {
      replace: true,
    });
  };

  const handleConfirmSections = (next: CrewFilePrintSectionSelection) => {
    setSections(next);
    syncSectionsUrl(next);
    setModalOpen(false);
  };

  if (!crewId) {
    return <Navigate to="/crew/list" replace />;
  }

  const backTo = moduleConfig.module.route.file(crewId);

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading crew file…
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load crew file.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to file
        </Link>
      </div>
    );
  }

  return (
    <>
      <ReportView
        backTo={backTo}
        backLabel="Back to file"
        title="Crew file print"
        subtitle={crewDisplayName(item)}
        printPageSize="A4 portrait"
        printMargin="0"
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setModalOpen(true)}
          >
            Sections ({selectedCount})
          </Button>
        }
      >
        <CrewFileReport
          item={item}
          companyLogoSrc={companyLogoSrc}
          companyName={managementCompany?.name}
          sections={sections}
          aiOverview={aiOverview}
        />
      </ReportView>

      <CrewFilePrintSectionsModal
        open={modalOpen}
        value={sections}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmSections}
        title="Print sections"
        confirmLabel="Apply"
      />
    </>
  );
};

export default CrewFileReportPage;
